import React, { useEffect, useState } from 'react';
import { X, MapPin, Calendar, Tag, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { LocalLink } from '../../i18n/useLocalizedNav';
import Stamp from './Stamp';

type Claim = {
  id: number;
  locationId: string;
  locationSlug: string;
  discountPercent: number | null;
  createdAt: string;
};

type Location = { id: string; slug: string; name: string; region: string | null };

type Props = {
  stamp: {
    id: string;
    name: string;
    multiplier: number | null;
    locations: Location[];
  };
  onClose: () => void;
};

const fmtDate = (iso: string) => {
  const d = new Date(iso.includes('T') || iso.includes(' ') ? iso.replace(' ', 'T') + 'Z' : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StampDetailsModal: React.FC<Props> = ({ stamp, onClose }) => {
  const { t } = useTranslation();
  const [claims, setClaims] = useState<Claim[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get(`/stamps/my/${stamp.id}/claims`);
        if (!alive) return;
        if (Array.isArray(data)) setClaims(data);
        else setError(t('sdm_error'));
      } catch {
        if (alive) setError(t('sdm_error'));
      }
    })();
    return () => {
      alive = false;
    };
  }, [stamp.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Resolve location name/region from the catalog passed in via stamp.locations.
  const locById = new Map(stamp.locations.map((l) => [l.id, l]));

  const total = stamp.multiplier ?? 0;
  const uniqueLocations = claims
    ? new Set(claims.map((c) => c.locationId)).size
    : null;
  const first = claims && claims.length ? claims[claims.length - 1] : null;
  const last = claims && claims.length ? claims[0] : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-brand-secondary/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl shadow-2xl my-auto max-h-[92vh] sm:max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-5 sm:p-7 border-b border-warm-200 flex items-start gap-4 sm:gap-5">
          <Stamp name={stamp.name} earned multiplier={stamp.multiplier} size={88} className="shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5 pt-1">
            <p className="font-heading font-bold text-[10px] sm:text-xs tracking-[0.2em] text-brand-accent uppercase">
              {t('sdm_earned_stamp')}
            </p>
            <h2 className="font-display text-2xl sm:text-3xl text-warm-800 leading-tight truncate">
              {stamp.name}
            </h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] sm:text-xs text-warm-500">
              <span>
                <strong className="text-warm-800">×{total}</strong> {t('sdm_total')}
              </span>
              {uniqueLocations != null && (
                <span>
                  <strong className="text-warm-800">{uniqueLocations}</strong>{' '}
                  {uniqueLocations === 1 ? t('sdm_venue') : t('sdm_venues')}
                </span>
              )}
              {first && last && first.id !== last.id && (
                <span>
                  {t('sdm_first', { date: fmtDate(first.createdAt) })}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full bg-warm-100 hover:bg-warm-200 flex items-center justify-center transition-colors"
            aria-label={t('sdm_close')}
          >
            <X className="w-5 h-5 text-warm-600" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-4">
          <p className="font-heading font-bold text-[10px] sm:text-xs tracking-[0.2em] text-warm-500 uppercase">
            {t('sdm_visit_history')}
          </p>

          {error && (
            <div className="bg-brand-danger/10 text-brand-danger p-3 rounded-xl text-xs font-bold border border-brand-danger/20">
              {error}
            </div>
          )}

          {claims === null && !error && (
            <div className="flex items-center justify-center py-10 text-warm-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}

          {claims && claims.length === 0 && !error && (
            <p className="text-warm-500 text-sm">
              {t('sdm_no_history')}
            </p>
          )}

          {claims && claims.length > 0 && (
            <ol className="space-y-3">
              {claims.map((c, i) => {
                const loc = locById.get(c.locationId);
                const visitNo = claims.length - i; // newest first → highest number first
                return (
                  <li
                    key={c.id}
                    className="bg-warm-100 rounded-2xl p-4 sm:p-5 border border-warm-200/70"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-2 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-brand-primary text-brand-secondary font-heading font-bold text-[11px]">
                            #{visitNo}
                          </span>
                          {loc ? (
                            <LocalLink
                              to={`/locations/${loc.slug}`}
                              className="font-heading font-bold text-warm-800 hover:text-brand-accent transition-colors truncate"
                            >
                              {loc.name}
                            </LocalLink>
                          ) : (
                            <span className="font-heading font-bold text-warm-800 truncate">
                              {t('sdm_unknown_venue')}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-warm-600">
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-warm-500" />
                            {fmtDate(c.createdAt)}
                          </span>
                          {loc?.region && (
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-warm-500" />
                              {loc.region}
                            </span>
                          )}
                          {c.discountPercent != null && c.discountPercent > 0 && (
                            <span className="inline-flex items-center gap-1.5 text-brand-success font-bold">
                              <Tag className="w-3.5 h-3.5" />
                              {t('sdm_discount', { percent: c.discountPercent })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default StampDetailsModal;
